// ============================================================================
// Polling helper
// ============================================================================
// Generic eventual-consistency wait used by browser harnesses to assert on the
// output of an in-flight Firebase trigger (the trigger writes to one
// collection in response to a write to another, and the test needs to wait
// for that write to propagate before asserting). Lives at the package root
// so callers don't drag in any Firebase SDK indirectly — they pass in their
// own fetch closure.

/**
 * Arguments for {@link pollUntil}.
 *
 * @typeParam Result - The type returned by `fetch`.
 */
export interface PollUntilArgs<Result> {
  /**
   * Async producer of the value to test. Called repeatedly until the
   * predicate returns true or the timeout expires.
   */
  fetch: () => Promise<Result>;
  /**
   * Returns true when the polled value satisfies the caller's condition.
   * The most recently fetched value is returned to the caller.
   */
  predicate: (value: Result) => boolean;
  /**
   * Maximum total wait, in milliseconds. The poll loop checks the deadline
   * after each `fetch` resolution; an in-flight `fetch` is allowed to
   * complete past the deadline, but a subsequent fetch will not start.
   */
  timeoutMs: number;
  /**
   * Delay between successive `fetch` calls, in milliseconds. Defaults to
   * 100ms. The first fetch fires immediately; the delay applies between
   * the resolution of one fetch and the start of the next.
   */
  intervalMs?: number;
  /**
   * Label used in the timeout error message. Defaults to `'pollUntil'`.
   */
  label?: string;
}

/**
 * Repeatedly invokes `fetch` until `predicate(result)` returns true or
 * `timeoutMs` elapses.
 *
 * @typeParam Result - The type produced by `fetch`.
 * @param args - See {@link PollUntilArgs}.
 * @return The most recently fetched value once the predicate is satisfied.
 * @throws Error tagged with `label` and the elapsed time when the timeout
 *   expires before the predicate ever becomes true.
 */
export async function pollUntil<Result>({
  fetch,
  predicate,
  timeoutMs,
  intervalMs = 100,
  label = 'pollUntil',
}: PollUntilArgs<Result>): Promise<Result> {
  const startedAtMs = Date.now();
  const deadlineMs = startedAtMs + timeoutMs;

  let latest: Result = await fetch();
  if (predicate(latest)) {
    return latest;
  }

  while (Date.now() < deadlineMs) {
    await new Promise(resolve => setTimeout(resolve, intervalMs));
    latest = await fetch();
    if (predicate(latest)) {
      return latest;
    }
  }

  const elapsedMs = Date.now() - startedAtMs;
  throw new Error(
    `[${label}] predicate not satisfied within ${String(timeoutMs)}ms (elapsed ${String(elapsedMs)}ms)`,
  );
}

/**
 * Canonical Firebase emulator services that browser harnesses may request.
 */
export const FIREBASE_EMULATOR_SERVICES = [
  'auth',
  'firestore',
  'functions',
  'database',
  'storage',
  'hosting',
  'pubsub',
] as const;

/**
 * Canonical browser/runtime worker capabilities that harnesses may request.
 */
export const WORKER_CAPABILITIES = ['sync-message', 'pwa'] as const;

/**
 * Default loopback host used by local browser harnesses when connecting to
 * Firebase emulators.
 */
export const FIREBASE_EMULATOR_DEFAULT_HOST = '127.0.0.1';

/**
 * Default Firebase emulator ports used by local browser harnesses.
 *
 * Keep these aligned with `firebase.json` for emulator-backed flows.
 */
export const FIREBASE_EMULATOR_DEFAULT_PORTS = {
  auth: 9099,
  firestore: 8081,
  functions: 5001,
  database: 9000,
  storage: 9199,
  pubsub: 8085,
} as const;

/**
 * Download token stamped on local review-video fixtures.
 *
 * The legacy SimTeach recording namespace has no reviewer read rule. The
 * emulator review seed uses this token so the local UI can play its synthetic
 * recording without weakening production Storage rules.
 */
export const CIPX_REVIEW_VIDEO_FIXTURE_DOWNLOAD_TOKEN =
  'cipx-review-video-fixture-2026';

/**
 * Default dev-server ports used by the browser harness apps and their
 * Playwright launches.
 */
export const BROWSER_TEST_DEV_SERVER_PORTS = {
  webapp: 3000,
  webappPlaywright: 3100,
  browserHarnessApp: 3001,
  browserHarnessAppPlaywright: 3101,
  codeRunner: 3002,
  codeRunnerPlaywright: 3102,
} as const;

/**
 * Central env var names used by local browser/emulator tests that need the
 * real notopenai Functions handler but must not call live OpenAI.
 */
export const NOTOPENAI_MOCK_OPENAI_ENV = {
  enabled: 'CIP_NOTOPENAI_MOCK_OPENAI',
  response: 'CIP_NOTOPENAI_MOCK_OPENAI_RESPONSE',
  totalTokens: 'CIP_NOTOPENAI_MOCK_OPENAI_TOTAL_TOKENS',
} as const;

/**
 * Default deterministic OpenAI mock settings for local Firebase
 * emulator-backed browser tests. The Functions handler still runs normally;
 * only its final OpenAI SDK call is replaced.
 */
export const NOTOPENAI_BROWSER_TEST_OPENAI_MOCK_ENV = {
  [NOTOPENAI_MOCK_OPENAI_ENV.enabled]: 'true',
  [NOTOPENAI_MOCK_OPENAI_ENV.response]: 'e2e-mocked',
  [NOTOPENAI_MOCK_OPENAI_ENV.totalTokens]: '7',
} as const;

/**
 * Stable query parameter name used to request Firebase emulator services.
 */
export const FIREBASE_QUERY_PARAM = 'firebase';

/**
 * Stable query parameter name used to request browser worker capabilities.
 */
export const WORKERS_QUERY_PARAM = 'workers';

/**
 * Stable query parameter name used to disable auto-run behavior.
 */
export const AUTORUN_QUERY_PARAM = 'autorun';

/**
 * Stable query parameter name used to inject a deterministic "now" into the
 * harness. Value must be an ISO 8601 datetime (e.g. `2026-04-23T12:00:00Z`).
 * Consumers can use this to freeze clocks for deterministic Playwright runs.
 */
export const TEST_TIME_QUERY_PARAM = 'testTime';

/**
 * Canonical Firebase emulator service identifier.
 */
export type FirebaseEmulatorService =
  (typeof FIREBASE_EMULATOR_SERVICES)[number];

/**
 * Canonical browser/runtime worker capability identifier.
 */
export type WorkerCapability = (typeof WORKER_CAPABILITIES)[number];

/**
 * Query fields supported by the shared browser-test capability contract.
 */
export type BrowserTestCapabilityQueryField =
  | 'firebase'
  | 'workers'
  | 'testTime';

/**
 * Structured validation error codes for the shared browser-test query contract.
 */
export type BrowserTestCapabilityQueryErrorCode =
  | 'invalid_value'
  | 'none_with_other_values'
  | 'invalid_datetime';

/**
 * Normalized capability request after parsing the query string.
 */
export interface BrowserTestCapabilityRequest {
  /** Requested Firebase emulator services in canonical order. */
  firebase: FirebaseEmulatorService[];
  /** Requested browser/runtime worker capabilities in canonical order. */
  workers: WorkerCapability[];
  /** Whether the page should auto-run its scenario logic. */
  autorun: boolean;
  /**
   * Frozen "now" for the run as an ISO 8601 string, or `null` when not
   * requested. Consumers (for example, the webapp's TimeContext) may use
   * this to bypass `Date.now()` / server-time queries for deterministic
   * Playwright runs.
   */
  testTimeIso: string | null;
}

/**
 * Raw query parameter values before tokenization or validation.
 */
export interface BrowserTestCapabilityRawQuery {
  /** Raw `firebase` query value. */
  firebase: string | null;
  /** Raw `workers` query value. */
  workers: string | null;
  /** Raw `autorun` query value. */
  autorun: string | null;
  /** Raw `testTime` query value. */
  testTime: string | null;
}

/**
 * Tokenized query values before validation or canonical ordering.
 */
export interface BrowserTestCapabilityParsedTokens {
  /** Parsed `firebase` tokens in query order. */
  firebase: string[];
  /** Parsed `workers` tokens in query order. */
  workers: string[];
  /** Parsed auto-run flag. */
  autorun: boolean;
  /** Parsed `testTime` ISO string, or `null` when absent/invalid. */
  testTimeIso: string | null;
}

/**
 * Structured validation error for the shared browser-test query contract.
 */
export interface BrowserTestCapabilityQueryError {
  /** Query field that produced the validation error. */
  field: BrowserTestCapabilityQueryField;
  /** Stable machine-readable error code. */
  code: BrowserTestCapabilityQueryErrorCode;
  /** Tokens involved in the error. */
  values: string[];
  /** Human-readable validation message. */
  message: string;
}

/**
 * Fully parsed query-contract result exposed to scenario harnesses.
 */
export interface ParsedBrowserTestCapabilityQuery {
  /** Raw query parameter strings. */
  raw: BrowserTestCapabilityRawQuery;
  /** Tokenized query values before validation. */
  parsed: BrowserTestCapabilityParsedTokens;
  /** Valid normalized capabilities in canonical order. */
  normalized: BrowserTestCapabilityRequest;
  /** Structured validation errors, if any. */
  errors: BrowserTestCapabilityQueryError[];
  /**
   * Capability request that is safe to apply.
   *
   * This is `null` whenever validation errors are present, so harnesses do not
   * need to guess how to handle partially valid requests.
   */
  resolvedRequest: BrowserTestCapabilityRequest | null;
}

/**
 * Capability support advertised by a specific browser app or scenario.
 */
export interface BrowserTestCapabilitySupport {
  /** Firebase emulator services that the current app/scenario can use. */
  firebase: FirebaseEmulatorService[];
  /** Worker capabilities that the current app/scenario can use. */
  workers: WorkerCapability[];
}

/**
 * Resolved capability plan for a specific app/scenario.
 */
export interface BrowserTestResolvedCapabilityPlan {
  /** Requested Firebase services that the app/scenario will apply. */
  firebase: FirebaseEmulatorService[];
  /** Requested worker capabilities that the app/scenario will apply. */
  workers: WorkerCapability[];
  /** Requested Firebase services not supported by the app/scenario. */
  unsupportedFirebase: FirebaseEmulatorService[];
  /** Requested worker capabilities not supported by the app/scenario. */
  unsupportedWorkers: WorkerCapability[];
  /**
   * Frozen "now" for the run as an ISO 8601 string, or `null` when not
   * requested. Passed through from the parsed request — the resolver does
   * not validate app-specific compatibility for time (all apps can honor
   * a frozen time).
   */
  testTimeIso: string | null;
}

/**
 * Parses and validates the shared browser-test capability query contract.
 */
export function parseBrowserTestCapabilityQuery(
  input: string | URLSearchParams,
): ParsedBrowserTestCapabilityQuery {
  const searchParams =
    typeof input === 'string' ? new URLSearchParams(input) : input;
  const raw: BrowserTestCapabilityRawQuery = {
    firebase: searchParams.get(FIREBASE_QUERY_PARAM),
    workers: searchParams.get(WORKERS_QUERY_PARAM),
    autorun: searchParams.get(AUTORUN_QUERY_PARAM),
    testTime: searchParams.get(TEST_TIME_QUERY_PARAM),
  };
  const testTimeResult = normalizeIsoDatetimeToken(raw.testTime);
  const parsed: BrowserTestCapabilityParsedTokens = {
    firebase: tokenizeCapabilityValue(raw.firebase),
    workers: tokenizeCapabilityValue(raw.workers),
    autorun: raw.autorun !== '0',
    testTimeIso: testTimeResult.value,
  };
  const firebaseResult = normalizeCapabilityTokens({
    field: 'firebase',
    tokens: parsed.firebase,
    allowedValues: FIREBASE_EMULATOR_SERVICES,
  });
  const workerResult = normalizeCapabilityTokens({
    field: 'workers',
    tokens: parsed.workers,
    allowedValues: WORKER_CAPABILITIES,
  });
  const normalized: BrowserTestCapabilityRequest = {
    firebase: firebaseResult.values,
    workers: workerResult.values,
    autorun: parsed.autorun,
    testTimeIso: testTimeResult.value,
  };
  const errors = [
    ...firebaseResult.errors,
    ...workerResult.errors,
    ...testTimeResult.errors,
  ];

  return {
    raw,
    parsed,
    normalized,
    errors,
    resolvedRequest: errors.length > 0 ? null : normalized,
  };
}

/**
 * Serializes a capability request back into canonical query parameters.
 */
export function serializeBrowserTestCapabilityRequest(
  request: BrowserTestCapabilityRequest,
): URLSearchParams {
  const searchParams = new URLSearchParams();

  searchParams.set(
    FIREBASE_QUERY_PARAM,
    request.firebase.length > 0 ? request.firebase.join(',') : 'none',
  );
  searchParams.set(
    WORKERS_QUERY_PARAM,
    request.workers.length > 0 ? request.workers.join(',') : 'none',
  );
  if (!request.autorun) {
    searchParams.set(AUTORUN_QUERY_PARAM, '0');
  }
  if (request.testTimeIso) {
    searchParams.set(TEST_TIME_QUERY_PARAM, request.testTimeIso);
  }

  return searchParams;
}

/**
 * Resolves a parsed capability request against app/scenario support.
 */
export function resolveBrowserTestCapabilityPlan({
  request,
  supported,
}: {
  request: BrowserTestCapabilityRequest | null;
  supported: BrowserTestCapabilitySupport;
}): BrowserTestResolvedCapabilityPlan | null {
  if (!request) {
    return null;
  }

  const supportedFirebase = new Set(supported.firebase);
  const supportedWorkers = new Set(supported.workers);

  return {
    firebase: request.firebase.filter(service =>
      supportedFirebase.has(service),
    ),
    workers: request.workers.filter(worker => supportedWorkers.has(worker)),
    unsupportedFirebase: request.firebase.filter(
      service => !supportedFirebase.has(service),
    ),
    unsupportedWorkers: request.workers.filter(
      worker => !supportedWorkers.has(worker),
    ),
    testTimeIso: request.testTimeIso,
  };
}

/**
 * Returns whether a parsed query is valid and safe to apply.
 */
export function hasBrowserTestCapabilityQueryErrors(
  parsedQuery: ParsedBrowserTestCapabilityQuery,
): boolean {
  return parsedQuery.errors.length > 0;
}

function tokenizeCapabilityValue(value: string | null): string[] {
  if (!value || value === 'none') {
    return [];
  }

  return value
    .split(',')
    .map(token => token.trim())
    .filter(token => token.length > 0);
}

function normalizeCapabilityTokens<TAllowedValue extends string>({
  field,
  tokens,
  allowedValues,
}: {
  field: BrowserTestCapabilityQueryField;
  tokens: string[];
  allowedValues: readonly TAllowedValue[];
}): {
  values: TAllowedValue[];
  errors: BrowserTestCapabilityQueryError[];
} {
  if (tokens.length === 0) {
    return {
      values: [],
      errors: [],
    };
  }

  const uniqueTokens = [...new Set(tokens)];
  const errors: BrowserTestCapabilityQueryError[] = [];

  if (uniqueTokens.includes('none')) {
    errors.push({
      field,
      code: 'none_with_other_values',
      values: uniqueTokens,
      message: `The "${field}" query cannot combine "none" with other values.`,
    });
  }

  const validValues = allowedValues.filter(value =>
    uniqueTokens.includes(value),
  );
  const invalidValues = uniqueTokens.filter(
    token =>
      token !== 'none' && !allowedValues.includes(token as TAllowedValue),
  );
  if (invalidValues.length > 0) {
    errors.push({
      field,
      code: 'invalid_value',
      values: invalidValues,
      message: `Unknown ${field} values: ${invalidValues.join(', ')}.`,
    });
  }

  return {
    values: validValues,
    errors,
  };
}

/**
 * Validates an ISO 8601 datetime token. Returns the canonical-form ISO
 * string on success, structured errors on failure, and a null value with
 * no errors when the token is absent. The canonical form is whatever
 * `Date.prototype.toISOString()` produces so downstream consumers get a
 * single normalized representation regardless of the input timezone
 * suffix.
 */
function normalizeIsoDatetimeToken(value: string | null): {
  value: string | null;
  errors: BrowserTestCapabilityQueryError[];
} {
  if (!value) {
    return {value: null, errors: []};
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return {
      value: null,
      errors: [
        {
          field: 'testTime',
          code: 'invalid_datetime',
          values: [value],
          message: `The "testTime" query must be a valid ISO 8601 datetime; got ${value}.`,
        },
      ],
    };
  }

  return {value: parsed.toISOString(), errors: []};
}
